import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Editor from "@monaco-editor/react";
import { RefreshCw, Calendar, User, X } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import Button from "../../components/ui/Button";
import TextInput from "../../components/ui/TextInput";
import NumberInput from "../../components/ui/NumberInput";
import SelectDropdown from "../../components/ui/SelectDropdown";
import StatusBadge from "../../components/ui/StatusBadge";
import Skeleton from "../../components/ui/Skeleton";
import TablePagination from "../../components/common/TablePagination";
import { getAuditLogs, getSystemLogs } from "../../services/systemLogsService";
import styles from "../../styles/LogsPage.module.css";
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { subDays, startOfMonth, endOfMonth, addDays } from 'date-fns';

const predefinedRanges = [
  { label: 'Today', value: [new Date(), new Date()] },
  { label: 'Yesterday', value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: 'Last 7 days', value: [subDays(new Date(), 6), new Date()] },
  { label: 'Last 30 days', value: [subDays(new Date(), 29), new Date()] },
  { label: 'This month', value: [startOfMonth(new Date()), new Date()] },
  { 
    label: 'Last month', 
    value: [
      startOfMonth(addDays(new Date(), -new Date().getDate())), 
      endOfMonth(addDays(new Date(), -new Date().getDate()))
    ] 
  },
];

const formatDateYyyyMmDd = (date) => {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTime = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  const yyyy = d.getFullYear();
  const MM = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${MM}-${dd} ${hh}:${mm}:${ss}`;
};

const SystemLogs = () => {
  const [mode, setMode] = useState("audit");
  const [lines, setLines] = useState(200);
  const [date, setDate] = useState(() => formatDateYyyyMmDd(new Date()));
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [auditLimit, setAuditLimit] = useState(10);
  const [auditOffset, setAuditOffset] = useState(0);
  const [rawLimit, setRawLimit] = useState(10);
  const [rawPage, setRawPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const [filters, setFilters] = useState({
    user_id: "",
    role: "",
    action: "",
    status: "",
    from: "",
    to: "",
  });

  const hasActiveFilters = useMemo(() => {
    if (mode === "audit") {
      return Boolean(filters.user_id || filters.role || filters.action || filters.status || filters.from || filters.to);
    }
    return Boolean(date !== formatDateYyyyMmDd(new Date()) || lines !== 200);
  }, [mode, filters, date, lines]);

  const handleReset = useCallback(() => {
    setFilters({
      user_id: "",
      role: "",
      action: "",
      status: "",
      from: "",
      to: "",
    });
    if (mode === "raw") {
      setDate(formatDateYyyyMmDd(new Date()));
      setLines(200);
    }
  }, [mode]);

  const logs = useMemo(() => payload?.logs || [], [payload]);


  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      if (mode === "raw") {
        const res = await getSystemLogs({
          lines,
          date: date || undefined,
        });
        setPayload(res);
      } else {
        const res = await getAuditLogs({
          user_id: filters.user_id || undefined,
          role: filters.role || undefined,
          action: filters.action || undefined,
          status: filters.status || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
          limit: auditLimit,
          offset: auditOffset,
        });
        setPayload(res);
      }
    } catch (error) {
      console.error("SYSTEM LOGS ERROR:", error);
      toast.error(error?.response?.data?.message || "Failed to load logs.");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [
    auditLimit,
    auditOffset,
    date,
    filters.action,
    filters.role,
    filters.status,
    filters.user_id,
    filters.from,
    filters.to,
    lines,
    mode,
  ]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (mode === "audit") {
      setAuditOffset(0);
    } else {
      setRawPage(1);
    }
  }, [mode]);

  useEffect(() => {
    setAuditOffset(0);
  }, [filters.user_id, filters.role, filters.action, filters.status, filters.from, filters.to, auditLimit, date]);

  const auditTotal = useMemo(() => Number(payload?.total ?? 0) || 0, [payload?.total]);
  const auditCurrentPage = useMemo(() => Math.floor(auditOffset / auditLimit) + 1, [auditLimit, auditOffset]);

  const rawOrdered = useMemo(() => logs.slice().reverse(), [logs]);
  const rawTotalPages = useMemo(() => Math.max(1, Math.ceil(rawOrdered.length / rawLimit)), [rawLimit, rawOrdered.length]);
  const rawPageSafe = useMemo(() => Math.min(Math.max(1, rawPage), rawTotalPages), [rawPage, rawTotalPages]);
  const rawOffset = useMemo(() => (rawPageSafe - 1) * rawLimit, [rawLimit, rawPageSafe]);
  const rawVisible = useMemo(() => rawOrdered.slice(rawOffset, rawOffset + rawLimit), [rawLimit, rawOffset, rawOrdered]);

  return (
    <AdminLayout title={mode === "audit" ? "Audit Logs" : "System Logs"}>
      <div className={styles.filtersCard} style={{ flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ width: '160px', flex: 'none' }}>
          <SelectDropdown 
            value={mode} 
            onChange={(e) => setMode(e.target.value)} 
            options={[
              { label: "Audit Logs", value: "audit" },
              { label: "Raw Logs", value: "raw" }
            ]}
          />
        </div>

        <div style={{ flex: 'none' }}>
          <DateRangePicker 
            format="yyyy-MM-dd"
            placeholder="Select Date Range"
            value={filters.from && filters.to ? [new Date(filters.from), new Date(filters.to)] : null}
            onChange={(val) => {
              if (val && val.length === 2) {
                setFilters(p => ({ ...p, from: formatDateYyyyMmDd(val[0]), to: formatDateYyyyMmDd(val[1]) }));
              } else {
                setFilters(p => ({ ...p, from: "", to: "" }));
              }
            }}
            ranges={predefinedRanges}
            placement="bottomStart"
            style={{ width: 240 }}
          />
        </div>

        {mode === "raw" ? (
          <>
            <div style={{ width: '160px', flex: 'none' }}>
              <TextInput
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                leadIcon={<Calendar size={15} />}
                title="Date"
              />
            </div>
            <div style={{ width: '110px', flex: 'none' }}>
              <NumberInput
                min={10}
                max={2000}
                value={lines}
                onChange={(e) => setLines(Number(e.target.value || 200))}
                placeholder="Lines"
                title="Lines"
              />
            </div>
          </>
        ) : (
          <>
            <div style={{ width: '140px', flex: 'none' }}>
              <TextInput
                placeholder="User ID..."
                value={filters.user_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, user_id: e.target.value }))}
                leadIcon={<User size={15} />}
                clearable
              />
            </div>
            <div style={{ width: '160px', flex: 'none' }}>
              <SelectDropdown
                value={filters.role}
                onChange={(e) => setFilters(p => ({ ...p, role: e.target.value }))}
                placeholder="All Roles"
                options={[
                  { label: "All Roles", value: "" },
                  { label: "Super Admin", value: "super-admin" },
                  { label: "Admin", value: "admin" },
                  { label: "Society Admin", value: "society-admin" },
                  { label: "Society Manager", value: "society-manager" },
                  { label: "Resident", value: "resident" },
                  { label: "Security Head", value: "security-head" },
                  { label: "Security Guard", value: "guard" },
                  { label: "Vendor", value: "vendor" }
                ]}
              />
            </div>
            <div style={{ width: '180px', flex: 'none' }}>
              <SelectDropdown
                value={filters.action}
                onChange={(e) => setFilters(p => ({ ...p, action: e.target.value }))}
                placeholder="All Actions"
                options={[
                  { label: "All Actions", value: "" },
                  { label: "LOGIN_SUCCESS", value: "LOGIN_SUCCESS" },
                  { label: "LOGIN_FAILED", value: "LOGIN_FAILED" },
                  { label: "VIEW_SYSTEM_LOGS", value: "VIEW_SYSTEM_LOGS" },
                  { label: "CREATE_USER", value: "CREATE_USER" },
                  { label: "UPDATE_USER", value: "UPDATE_USER" },
                  { label: "DELETE_USER", value: "DELETE_USER" },
                  { label: "CREATE_INVITE", value: "CREATE_INVITE" },
                  { label: "CREATE_TICKET", value: "CREATE_TICKET" },
                  { label: "UPDATE_TICKET", value: "UPDATE_TICKET" },
                  { label: "CLOSE_TICKET", value: "CLOSE_TICKET" },
                  { label: "CREATE_NOTICE", value: "CREATE_NOTICE" },
                  { label: "UPDATE_SETTINGS", value: "UPDATE_SETTINGS" },
                  { label: "APPROVE_VISITOR", value: "APPROVE_VISITOR" },
                  { label: "CHECK_IN_VISITOR", value: "CHECK_IN_VISITOR" },
                  { label: "CHECK_OUT_VISITOR", value: "CHECK_OUT_VISITOR" }
                ]}
              />
            </div>
            <div style={{ width: '150px', flex: 'none' }}>
              <SelectDropdown
                value={filters.status}
                onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
                placeholder="All Statuses"
                options={[
                  { label: "All Statuses", value: "" },
                  { label: "SUCCESS", value: "SUCCESS" },
                  { label: "ERROR", value: "ERROR" },
                  { label: "FAIL", value: "FAIL" },
                  { label: "DENIED", value: "DENIED" },
                  { label: "WARN", value: "WARN" },
                  { label: "PENDING", value: "PENDING" }
                ]}
              />
            </div>
          </>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button 
            variant="outline"
            size="sm"
            onClick={handleReset} 
            title="Reset filters"
            disabled={!hasActiveFilters}
          >
            <X size={15} />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadLogs} 
            title="Refresh logs"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "32px 16px" }}>
          <Skeleton variant="table" count={5} />
        </div>
      ) : (
        <>
          <div className={styles.tableCard}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  {mode === "raw" ? (
                    <>
                      <th className={styles.th}>Time</th>
                      <th className={styles.th}>Level</th>
                      <th className={styles.th}>Message</th>
                      <th className={styles.th}>Request</th>
                      <th className={styles.th}>User</th>
                      <th className={styles.th}>Status</th>
                      <th className={styles.th}>Path</th>
                    </>
                  ) : (
                    <>
                      <th className={styles.th}>Time</th>
                      <th className={styles.th}>User</th>
                      <th className={styles.th}>Role</th>
                      <th className={styles.th}>Action</th>
                      <th className={styles.th}>Description</th>
                      <th className={styles.th}>Status</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={mode === "raw" ? 7 : 6} className={styles.td} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                      No data available
                    </td>
                  </tr>
                ) : mode === "raw" ? (
                  rawVisible.map((entry, idx) => {
                    const rowId = `raw-${entry.ts || "ts"}-${idx}`;
                    const isExpanded = expandedRow === rowId;
                    const rowClass = [styles.tbodyRow, isExpanded ? styles.activeRow : ""].filter(Boolean).join(" ");

                    return (
                      <React.Fragment key={rowId}>
                        <tr className={rowClass} onClick={() => toggleRow(rowId)}>
                          <td className={[styles.td, styles.nowrap, styles.mono].join(" ")}>{formatDateTime(entry.ts)}</td>
                          <td className={[styles.td, styles.nowrap].join(" ")}>{entry.level || "-"}</td>
                          <td className={[styles.td, styles.wrap].join(" ")}>{entry.message || "-"}</td>
                          <td className={[styles.td, styles.nowrap, styles.mono].join(" ")}>{entry.requestId || "-"}</td>
                          <td className={[styles.td, styles.nowrap].join(" ")}>
                            {entry.userName || (entry.userId ? `${entry.userId}` : "-")}
                            {entry.userType ? ` (${entry.userType})` : ""}
                          </td>
                          <td className={[styles.td, styles.nowrap].join(" ")}>{entry.statusCode ?? "-"}</td>
                          <td className={[styles.td, styles.wrap, styles.mono].join(" ")}>{entry.path || "-"}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0 }}>
                              <div className={styles.expandedContainer}>
                                <div className={styles.expandedHeader}>
                                  <div className={styles.expandedHeaderTitle}>
                                    <span className={styles.expandedHeaderTitleBold}>{String(entry.level || "INFO").toUpperCase()}</span>
                                    <span className={styles.expandedHeaderTitleSemi}>{entry.module || "System"}</span>
                                    • {formatDateTime(entry.ts)} (Trace: {entry.requestId || "N/A"})
                                  </div>
                                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setExpandedRow(null); }}>Dismiss View</Button>
                                </div>
                                <div className={styles.editorWrap}>
                                  <Editor height="300px" defaultLanguage="json" theme="vs-dark" value={JSON.stringify(entry, null, 2)} options={{ readOnly: true, minimap: { enabled: false }, wordWrap: "on" }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  logs.map((entry) => {
                    const rowId = `audit-${entry.id}`;
                    const isExpanded = expandedRow === rowId;
                    const rowClass = [styles.tbodyRow, isExpanded ? styles.activeRow : ""].filter(Boolean).join(" ");

                    return (
                      <React.Fragment key={rowId}>
                        <tr className={rowClass} onClick={() => toggleRow(rowId)}>
                          <td className={[styles.td, styles.nowrap, styles.mono].join(" ")}>{formatDateTime(entry.created_at)}</td>
                          <td className={[styles.td, styles.nowrap].join(" ")}>{entry.user_name || entry.user_id || "-"}</td>
                          <td className={[styles.td, styles.nowrap].join(" ")}>{entry.role || "-"}</td>
                          <td className={[styles.td, styles.nowrap, styles.mono].join(" ")}>{entry.action || "-"}</td>
                          <td className={[styles.td, styles.wrap].join(" ")}>{entry.description || "-"}</td>
                          <td className={styles.td}>
                            <StatusBadge
                              status={
                                String(entry.status || "").toUpperCase().includes("SUCCESS") ? "approved"
                                : String(entry.status || "").toUpperCase().includes("FAIL") || String(entry.status || "").toUpperCase().includes("ERROR") || String(entry.status || "").toUpperCase().includes("DENIED") ? "rejected"
                                : String(entry.status || "").toUpperCase().includes("WARN") ? "warning"
                                : "neutral"
                              }
                              label={entry.status || "-"}
                            />
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} style={{ padding: 0 }}>
                              <div className={styles.expandedContainer}>
                                <div className={styles.expandedHeader}>
                                  <div className={styles.expandedHeaderTitle}>
                                    <span className={styles.expandedHeaderTitleBold}>{String(entry.status || "INFO").toUpperCase()}</span>
                                    <span className={styles.expandedHeaderTitleSemi}>{entry.module || "Audit"}</span>
                                    • {formatDateTime(entry.created_at)} (Trace: {entry.id || "N/A"})
                                  </div>
                                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setExpandedRow(null); }}>Dismiss View</Button>
                                </div>
                                <div className={styles.editorWrap}>
                                  <Editor height="300px" defaultLanguage="json" theme="vs-dark" value={JSON.stringify(entry, null, 2)} options={{ readOnly: true, minimap: { enabled: false }, wordWrap: "on" }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {mode === "audit" ? (
            <TablePagination
              totalItems={auditTotal}
              page={auditCurrentPage}
              limit={auditLimit}
              onPageChange={(newPage) => setAuditOffset((newPage - 1) * auditLimit)}
              onLimitChange={(newLimit) => {
                setAuditLimit(newLimit);
                setAuditOffset(0);
              }}
              limitOptions={[5, 10, 25, 50, 100]}
            />
          ) : (
            <TablePagination
              totalItems={rawOrdered.length}
              page={rawPageSafe}
              limit={rawLimit}
              onPageChange={(newPage) => setRawPage(newPage)}
              onLimitChange={(newLimit) => {
                setRawLimit(newLimit);
                setRawPage(1);
              }}
              limitOptions={[5, 10, 25, 50, 100]}
            />
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default SystemLogs;
